import { useState, useEffect, useCallback } from 'react'
import { api } from '../lib/api'
import { Empty } from '../components/UI'
import { useAuth } from '../hooks/useAuth'

const OPS_STAT_LABELS = {
  products_total: 'Products', products_approved: 'Approved',
  funnels_ready: 'Funnels Ready', creatives_ready: 'Creatives Ready',
  campaigns_live: 'Campaigns Live', completed_7d: 'Completed (7d)',
}
const PERSONAL_STAT_LABELS = {
  my_open: 'My Open Tasks', my_overdue: 'My Overdue',
  my_due_week: 'Due This Week', my_completed_7d: 'Completed by Me (7d)',
}
const SECTION_NAMES = {
  products:'Product Research', funnel_building:'Funnel Building', funnel:'Funnel Building',
  creatives:'Creatives', ad_copy:'Ad Copy', adcopy:'Ad Copy', ad_scheduling:'Ad Scheduling',
  scheduling:'Ad Scheduling', asset_prep:'Asset Prep', assetprep:'Asset Prep',
  creative_requests:'Creative Requests', creativerequests:'Creative Requests',
}
const ACTION_VERB = { login:'signed in', create:'created', edit:'updated', delete:'deleted' }

export default function Dashboard({ setActive }) {
  const { user } = useAuth()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  const load = useCallback(() => {
    setLoading(true)
    api.getDashboard().then(setData).catch(() => setData(null)).finally(() => setLoading(false))
  }, [])
  useEffect(() => { load() }, [load])

  const timeAgo = (ts) => {
    try {
      const diff = (Date.now() - new Date(ts).getTime()) / 1000
      if (diff < 60) return 'just now'
      if (diff < 3600) return Math.floor(diff/60) + 'm ago'
      if (diff < 86400) return Math.floor(diff/3600) + 'h ago'
      return Math.floor(diff/86400) + 'd ago'
    } catch { return '' }
  }
  const firstName = (user?.full_name || '').split(' ')[0] || 'there'
  const hour = new Date().getHours()
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening'

  if (loading) return <div style={{padding:40}}><Empty icon="⏳" title="Loading your dashboard..." /></div>
  if (!data) return <div style={{padding:40}}><Empty icon="📊" title="Dashboard unavailable" sub="Could not load your data." /></div>

  const totalTasks = (data.actionable||[]).reduce((s,a)=>s+a.count,0)

  return (
    <div style={{flex:1,overflow:'auto',padding:'24px 28px'}}>
      {/* Header */}
      <div style={{marginBottom:24}}>
        <div style={{fontSize:22,fontWeight:700,color:'var(--text)'}}>{greeting}, {firstName}</div>
        <div style={{fontSize:13,color:'var(--text3)',marginTop:4}}>
          {totalTasks === 0 ? "You're all caught up — nothing needs attention." : `${totalTasks} item${totalTasks!==1?'s':''} need${totalTasks===1?'s':''} your attention`}
        </div>
      </div>

      {/* Stats strip */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))',gap:10,marginBottom:26}}>
        {Object.entries(data.statsMode === 'personal' ? PERSONAL_STAT_LABELS : OPS_STAT_LABELS).map(([k,label]) => {
          const v = data.stats?.[k] ?? 0
          const alert = k === 'my_overdue' && v > 0
          return (
            <div key={k} style={{background:'var(--bg2)',border:'1px solid '+(alert?'rgba(239,68,68,0.35)':'var(--border)'),borderRadius:10,padding:'12px 14px'}}>
              <div style={{fontSize:9,color:'var(--text3)',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.8px',fontFamily:'DM Mono,monospace'}}>{label}</div>
              <div style={{fontSize:22,fontWeight:700,color:alert?'#EF4444':'var(--text)',marginTop:4,fontFamily:'DM Mono,monospace'}}>{v}</div>
            </div>
          )
        })}
      </div>

      <div style={{display:'grid',gridTemplateColumns:'minmax(0,2fr) minmax(0,1fr)',gap:20,alignItems:'start'}}>
        {/* Actionable cards */}
        <div>
          <div style={{fontSize:11,fontWeight:700,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'1px',fontFamily:'DM Mono,monospace',marginBottom:10}}>Needs Attention</div>
          {(data.actionable||[]).length === 0
            ? <div style={{background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:12,padding:32,textAlign:'center'}}>
                <div style={{fontSize:30,marginBottom:8}}>✓</div>
                <div style={{fontSize:14,fontWeight:600,color:'var(--text)'}}>All clear</div>
                <div style={{fontSize:12,color:'var(--text3)',marginTop:4}}>No overdue or pending items.</div>
              </div>
            : <div style={{display:'flex',flexDirection:'column',gap:12}}>
                {data.actionable.map(card => {
                  const urgent = card.key.includes('overdue')
                  return (
                    <div key={card.key} style={{background:'var(--bg2)',border:'1px solid '+(urgent?'rgba(239,68,68,0.35)':'var(--border)'),borderRadius:12,overflow:'hidden'}}>
                      <div onClick={() => setActive && setActive(card.section)}
                        style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'11px 14px',cursor:'pointer',borderBottom:'1px solid var(--border)'}}>
                        <div style={{display:'flex',alignItems:'center',gap:9}}>
                          <span style={{fontSize:15}}>{card.icon}</span>
                          <span style={{fontSize:13.5,fontWeight:700,color:urgent?'#EF4444':'var(--text)'}}>{card.title}</span>
                          <span style={{fontSize:11,fontWeight:700,color:'#fff',background:urgent?'#EF4444':'var(--accent)',borderRadius:11,padding:'1px 8px'}}>{card.count}</span>
                        </div>
                        <span style={{fontSize:11,color:'var(--accent)',fontWeight:600}}>Open →</span>
                      </div>
                      <div style={{maxHeight:150,overflow:'auto'}}>
                        {card.items.slice(0,6).map((it,i) => (
                          <div key={i} style={{display:'flex',justifyContent:'space-between',padding:'7px 14px',borderBottom:i<Math.min(card.items.length,6)-1?'1px solid var(--border)':'none',fontSize:12}}>
                            <span style={{color:'var(--text2)'}}>{it.label}</span>
                            <span style={{color:'var(--text3)',fontSize:11,whiteSpace:'nowrap',marginLeft:10}}>{it.sub}</span>
                          </div>
                        ))}
                        {card.items.length > 6 && <div style={{padding:'6px 14px',fontSize:11,color:'var(--text3)'}}>+{card.items.length-6} more…</div>}
                      </div>
                    </div>
                  )
                })}
              </div>}
        </div>

        {/* Right panel: outcomes for researchers, deadlines for everyone else */}
        {data.rightPanel === 'outcomes' ? (
        <div>
          <div style={{fontSize:11,fontWeight:700,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'1px',fontFamily:'DM Mono,monospace',marginBottom:10}}>Recent Decisions</div>
          <div style={{background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:12,overflow:'hidden'}}>
            {(data.outcomes||[]).length === 0
              ? <div style={{padding:22,textAlign:'center',fontSize:12,color:'var(--text3)'}}>No decisions yet</div>
              : data.outcomes.map((o,i) => {
                  const c = o.status === 'Approved' ? 'var(--accent3)' : o.status === 'Rejected' ? '#EF4444' : '#F59E0B'
                  const icon = o.status === 'Approved' ? '✓' : o.status === 'Rejected' ? '✕' : '↻'
                  return (
                    <div key={i} onClick={() => setActive && setActive('products')}
                      style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:8,padding:'9px 13px',borderBottom:i<data.outcomes.length-1?'1px solid var(--border)':'none',cursor:'pointer'}}>
                      <div style={{display:'flex',alignItems:'center',gap:8,minWidth:0}}>
                        <span style={{fontSize:12,fontWeight:700,color:c}}>{icon}</span>
                        <span style={{fontSize:12,color:'var(--text2)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{o.label}</span>
                      </div>
                      <span style={{fontSize:10,fontWeight:700,color:c,fontFamily:'DM Mono,monospace',whiteSpace:'nowrap'}}>{o.status}</span>
                    </div>
                  )
                })}
          </div>
        </div>
        ) : (
        <div>
          <div style={{fontSize:11,fontWeight:700,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'1px',fontFamily:'DM Mono,monospace',marginBottom:10}}>Due This Week</div>
          <div style={{background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:12,overflow:'hidden'}}>
            {(data.upcoming||[]).length === 0
              ? <div style={{padding:22,textAlign:'center',fontSize:12,color:'var(--text3)'}}>Nothing due in the next 7 days</div>
              : data.upcoming.map((u,i) => {
                  const d = new Date(u.deadline + 'T00:00:00')
                  const today0 = new Date(new Date().toISOString().slice(0,10) + 'T00:00:00')
                  const days = Math.round((d - today0) / 86400000)
                  const when = days === 0 ? 'Today' : days === 1 ? 'Tomorrow' : d.toLocaleDateString(undefined,{weekday:'short'})
                  const urgent = days <= 1
                  return (
                    <div key={i} onClick={() => setActive && setActive(u.section)}
                      style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:8,padding:'9px 13px',borderBottom:i<data.upcoming.length-1?'1px solid var(--border)':'none',cursor:'pointer'}}>
                      <div style={{display:'flex',alignItems:'center',gap:8,minWidth:0}}>
                        <span style={{fontSize:12}}>{u.icon}</span>
                        <span style={{fontSize:12,color:'var(--text2)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{u.label}</span>
                      </div>
                      <span style={{fontSize:10,fontWeight:700,color:urgent?'#F59E0B':'var(--text3)',fontFamily:'DM Mono,monospace',whiteSpace:'nowrap'}}>{when}</span>
                    </div>
                  )
                })}
          </div>
        </div>
        )}
      </div>
    </div>
  )
}
